import {doppeActionTypeDefineViewerMetadata} from "../../client-server-common/doppe-action-types/doppe-action-type";
import {doppeActionTypeLinkView} from "./doppe-action-type-link-view";
import React from 'react';
import {
    doppeLinkSpecIsEntity,
    doppeLinkSpecIsPage,
    doppeLinkSpecIsUrl,
    DoppeLinkSpecEntityType
} from "../../client-server-common/types/doppe-action-cta-button";
import {
    bymoPageGetInnerNavigatablePageById,
    DoppeDtoInnerPageType
} from "../../client-server-common/types/doppe-dto-inner-page";
import {DoppeDtoStoreProductType} from "../../client-server-common/types/doppe-dto-store-product";
import {DoppeDtoBookingServiceType} from "../../client-server-common/types/doppe-dto-booking-service";
import {DoppeDtoEventRegistrationType} from "../../client-server-common/types/doppe-dto-event";
import type {IDoppeViewerActionContext} from "../../doppe-sdk/doppe-viewer-action-context/doppe-viewer-action-context";
import {bymoPageContextPropsResolveEntityInfo} from "../../doppe-sdk/bymo-page/bymo-page-context-props";
import {DoppeViewerLang} from "../../client-server-common/doppe-viewer/doppe-viewer-lang";
import {
    viewerEntityNavigationResolveBlogPostTarget,
    viewerEntityNavigationResolveEventTarget,
    viewerEntityNavigationResolveProductTarget,
    viewerEntityNavigationResolveServiceTarget,
    viewerListNavigationResolveProductsListTarget,
    viewerListNavigationResolveServicesListTarget,
    viewerListNavigationResolveBlogPostsListTarget,
    viewerListNavigationResolveEventsListTarget
} from '../../client-server-common/viewer-pages/viewer-entity-navigation';

/**
 * Helper function to navigate to a page using the router.
 * For vertical list pages, checks the useHoppVertical*ListPage settings.
 */
function navigateToPageViaRouter(
    pageId: string,
    actionContext: IDoppeViewerActionContext<any>
) {
    actionContext.performContextCallback(async ({clientServices, bymoPageProps}) => {
        const router = clientServices.viewerRouterClientService;
        const page = bymoPageGetInnerNavigatablePageById(pageId, bymoPageProps);
        const bymoPageContextProps = actionContext.getBymoPageContextProps();
        
        // Create navigation options for list pages
        const listNavigationOptions = {
            bymoPageContextProps: bymoPageContextProps,
            viewerLang: DoppeViewerLang.English,
            showMobileView: false,
            isLivePreviewMode: false
        };
        
        if (page) {
            switch (page.type) {
                case DoppeDtoInnerPageType.Products: {
                    const result = viewerListNavigationResolveProductsListTarget(listNavigationOptions);
                    if (result.useInternalRouting) {
                        router.navigateToProductsList();
                    } else if (result.externalUrl) {
                        window.location.href = result.externalUrl;
                    }
                    break;
                }
                case DoppeDtoInnerPageType.BlogPosts: {
                    const result = viewerListNavigationResolveBlogPostsListTarget(listNavigationOptions);
                    if (result.useInternalRouting) {
                        router.navigateToBlogPostsList();
                    } else if (result.externalUrl) {
                        window.location.href = result.externalUrl;
                    }
                    break;
                }
                case DoppeDtoInnerPageType.Events: {
                    const result = viewerListNavigationResolveEventsListTarget(listNavigationOptions);
                    if (result.useInternalRouting) {
                        router.navigateToEventsList();
                    } else if (result.externalUrl) {
                        window.location.href = result.externalUrl;
                    }
                    break;
                }
                case DoppeDtoInnerPageType.Services: {
                    const result = viewerListNavigationResolveServicesListTarget(listNavigationOptions);
                    if (result.useInternalRouting) {
                        router.navigateToServicesList();
                    } else if (result.externalUrl) {
                        window.location.href = result.externalUrl;
                    }
                    break;
                }
                case DoppeDtoInnerPageType.Cart: {
                    router.navigateToCart();
                    break;
                }
                case DoppeDtoInnerPageType.Content:
                default:
                    router.navigateToContentPage(page.slug);
                    break;
            }
        }
    });
}

/**
 * Helper function to navigate to an entity item page using the router.
 * Uses useHoppVerticalItemPages flag to determine internal vs external navigation.
 */
function navigateToEntityViaRouter(
    entityType: DoppeLinkSpecEntityType,
    entityId: string,
    actionContext: IDoppeViewerActionContext<any>
) {
    actionContext.performContextCallback(async ({clientServices}) => {
        const router = clientServices.viewerRouterClientService;
        const bymoPageContextProps = actionContext.getBymoPageContextProps();
        
        // Resolve entity info from business entities data (includes slug and pageUrl)
        const entityInfo = bymoPageContextPropsResolveEntityInfo(
            bymoPageContextProps,
            entityType,
            entityId
        );
        const slug = entityInfo.slug;
        
        // Create navigation options
        const navigationOptions = {
            bymoPageContextProps: bymoPageContextProps,
            viewerLang: DoppeViewerLang.English,
            showMobileView: false,
            isLivePreviewMode: false
        };
        
        switch (entityType) {
            case DoppeLinkSpecEntityType.Product: {
                const result = viewerEntityNavigationResolveProductTarget(
                    {slug: slug, productPageUrl: entityInfo.pageUrl},
                    navigationOptions
                );
                if (result.useInternalRouting && result.slug) {
                    // Route to the appropriate page based on product type
                    if (entityInfo.innerType === DoppeDtoStoreProductType.Digital) {
                        router.navigateToDigitalProductItem(result.slug);
                    } else {
                        // Default to physical product for physical/unspecified types
                        router.navigateToProductItem(result.slug);
                    }
                } else if (result.externalUrl) {
                    // Not in live preview mode, so this will navigate in same tab
                    window.location.href = result.externalUrl;
                }
                break;
            }
            case DoppeLinkSpecEntityType.BlogPost: {
                const result = viewerEntityNavigationResolveBlogPostTarget(
                    {slug: slug, pageUrl: entityInfo.pageUrl},
                    navigationOptions
                );
                if (result.useInternalRouting && result.slug) {
                    router.navigateToBlogPostItem(result.slug);
                } else if (result.externalUrl) {
                    window.location.href = result.externalUrl;
                }
                break;
            }
            case DoppeLinkSpecEntityType.Event: {
                const result = viewerEntityNavigationResolveEventTarget(
                    {slug: slug, pageUrl: entityInfo.pageUrl},
                    navigationOptions
                );
                if (result.useInternalRouting && result.slug) {
                    // Route to the appropriate page based on event registration type
                    if (entityInfo.innerType === DoppeDtoEventRegistrationType.Ticketing) {
                        router.navigateToTicketingEventItem(result.slug);
                    } else {
                        // Default to RSVP event for RSVP/Unknown types
                        router.navigateToEventItem(result.slug);
                    }
                } else if (result.externalUrl) {
                    window.location.href = result.externalUrl;
                }
                break;
            }
            case DoppeLinkSpecEntityType.Service: {
                const result = viewerEntityNavigationResolveServiceTarget(
                    {slug: slug, servicePageUrl: entityInfo.pageUrl},
                    navigationOptions
                );
                if (result.useInternalRouting && result.slug) {
                    // Route to the appropriate page based on service type
                    switch (entityInfo.innerType) {
                        case DoppeDtoBookingServiceType.COURSE:
                            router.navigateToCourseItem(result.slug);
                            break;
                        case DoppeDtoBookingServiceType.Class:
                            router.navigateToClassItem(result.slug);
                            break;
                        case DoppeDtoBookingServiceType.APPOINTMENT:
                        default:
                            // Default to appointment for unknown/unspecified types
                            router.navigateToAppointmentItem(result.slug);
                            break;
                    }
                } else if (result.externalUrl) {
                    window.location.href = result.externalUrl;
                }
                break;
            }
        }
    });
}

export const doppeActionTypeLinkViewViewer = doppeActionTypeDefineViewerMetadata(doppeActionTypeLinkView, {
    loadViewerComponents: async () => {
        return {
            DoppeActionTypeLinkViewWidget: (await import('./doppe-action-type-link-view-widget/doppe-action-type-link-view-widget')).DoppeActionTypeLinkViewWidget
        }
    },

    renderWidget: (renderProps, viewerComponents) => {

        const {
            DoppeActionTypeLinkViewWidget
        } = viewerComponents;

        return (
            <DoppeActionTypeLinkViewWidget
                {...renderProps}
            />
        );

    },

    getActionButtonProps: _actionContext => {
        // Widget opener - button props not needed here
        return {};
    },

    activateCTA: actionContext => {
        const url = actionContext.action.settings.url;
        
        // Handle Page link spec - navigate using router
        if (doppeLinkSpecIsPage(url)) {
            const pageId = url.pageId;
            if (pageId) {
                navigateToPageViaRouter(pageId, actionContext);
            }
            return;
        }
        
        // Handle Entity link spec - navigate to entity item page via router
        if (doppeLinkSpecIsEntity(url)) {
            const entityType = url.entityType;
            const entityId = url.entityId;
            if (entityType && entityId) {
                navigateToEntityViaRouter(entityType, entityId, actionContext);
            }
            return;
        }
        
        // Handle URL string - open in new tab
        if (doppeLinkSpecIsUrl(url)) {
            actionContext.navigateToUrl(url, true);
        }
    }
})